import { computed, makeAutoObservable } from "mobx";

import {
  SIDEBAR_DEFAULT_WIDTH,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MOBILE_MAX_WIDTH,
} from "../types/sidebar";
import { networkEventManager } from "../services/networkEventManager";
import {
  apiService,
  ApiKeySetting,
  ApiKeySettingItem,
  ChatSearchResult,
} from "../services/apiService";
import { IContractPreviewItem, IPolicyPreviewItem } from "../types/chat";
import type MainStore from "./MainStore";
import { API_CONFIG } from "../services";
import { ContactSupportFormType } from "../config/constants";
import type { ITicket } from "../types/tickets";
import { WS_EVENTS } from "../types/wsEvents";
import { APP_CONFIG_FLAGS } from "../types/app";
import { IFCHeaderCapabilities } from "../types/auth";

const HEADER_CAPABILITY_KEYS: (keyof IFCHeaderCapabilities)[] = [
  "home",
  "claims",
  "dashboard",
  "policies",
  "contracts",
];

const toHeaderFlagBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  return undefined;
};

/**
 * The backend allows string/number/list config flags too, so a misconfigured
 * APP_TABS_VISIBILITY_CONTROLLER must not be trusted as-is: only a plain object
 * is accepted, "true"/"false" strings are coerced, and any other value is dropped
 * (so the tab falls back to its default in useFCCapabilities).
 */
const parseHeaderConfigFlags = (
  raw: unknown,
): Partial<IFCHeaderCapabilities> | undefined => {
  if (raw === undefined || raw === null) return undefined;

  if (typeof raw !== "object" || Array.isArray(raw)) {
    console.warn(
      `[AppStore] ${APP_CONFIG_FLAGS.APP_TABS_VISIBILITY_CONTROLLER} must be an object, got ${Array.isArray(raw) ? "array" : typeof raw}; ignoring it`,
    );
    return undefined;
  }

  const flags: Partial<IFCHeaderCapabilities> = {};
  const source = raw as Record<string, unknown>;

  HEADER_CAPABILITY_KEYS.forEach((key) => {
    if (!(key in source)) return;

    const value = toHeaderFlagBoolean(source[key]);
    if (value === undefined) {
      console.warn(
        `[AppStore] ${APP_CONFIG_FLAGS.APP_TABS_VISIBILITY_CONTROLLER}.${key} must be a boolean, got ${JSON.stringify(source[key])}; ignoring it`,
      );
      return;
    }

    flags[key] = value;
  });

  return flags;
};

export type ContactSupportUploadFile = {
  id: string;
  file: File;
  status: "attached" | "uploading" | "uploaded" | "error";
};

export default class AppStore {
  mainStore: MainStore | null = null;

  /** Current user email from AuthProvider (session) */
  userEmail: string | null = null;

  // Sidebar states
  isCollapsed: boolean = false;
  isResizing: boolean = false;
  sidebarWidth: number = SIDEBAR_DEFAULT_WIDTH;

  // Search states
  searchQuery: string = "";
  searchResults: ChatSearchResult[] = [];
  searchLoading: boolean = false;

  // Policy states
  policyToPreview: IPolicyPreviewItem | null = null;
  // Contract states
  contractToPreview: IContractPreviewItem | null = null;

  // Contact support upload files with status
  contactSupportUploadFiles: ContactSupportUploadFile[] = [];
  isUploadSuccessful: boolean = false;

  /** Frontend-tagged config flags pushed over the WebSocket (on connect and on change) */
  configFlags: Record<string, unknown> = {};
  /** True once the first config_flags snapshot has arrived over the WebSocket */
  isConfigFlagsLoaded: boolean = false;

  /** Set when the backend reports the preauth bound-chat link has expired (410 link_expired) */
  isPreauthExpired: boolean = false;

  /** Set when another browser tab has claimed active status; this tab's websocket is closed until refresh */
  isTabInactive: boolean = false;
  // Api key settings
  apiKeySetting: ApiKeySetting | null = null;
  isApiKeySettingLoading: boolean = false;

  // Full secret returned by a rotation; shown once, then discarded
  rotatedApiKeySecret: string | null = null;
  isRotatingApiKey: boolean = false;

  isUpdatingApiKeyAllowlist: boolean = false;
  apiKeyAllowlistError: string | null = null;

  constructor(mainStore: MainStore) {
    makeAutoObservable(this, {
      mainStore: false,
      // Structural so a freshly parsed but equal object doesn't re-trigger consumers.
      headerConfigFlags: computed.struct,
    });
    this.mainStore = mainStore;
    this.setLocalStorageData();

    if (typeof window !== "undefined") {
      window.addEventListener(WS_EVENTS.CONFIG_FLAGS_UPDATE, (e: Event) => {
        this.setConfigFlags((e as CustomEvent).detail ?? {});
      });
    }

    networkEventManager.on("auth:link_expired", () => {
      this.setPreauthExpired(true);
    });

    networkEventManager.on("tab:deactivated", () => {
      this.setTabInactive(true);
    });
  }

  setConfigFlags = (flags: Record<string, unknown>) => {
    this.configFlags = flags;
    this.isConfigFlagsLoaded = true;
  };

  setPreauthExpired = (expired: boolean) => {
    this.isPreauthExpired = expired;
  };

  setTabInactive = (inactive: boolean) => {
    this.isTabInactive = inactive;
  };

  get isSidebarMobile(): boolean {
    return this.sidebarWidth <= SIDEBAR_MOBILE_MAX_WIDTH;
  }

  // NOTE: Always check for user location when using this getter to ensure it's not hidden on claims page
  get isSidebarHidden(): boolean {
    return !this.mainStore?.claimsStore?.claimToPreview;
  }

  get isHomeV2Enabled(): boolean {
    return Boolean(this?.configFlags?.[APP_CONFIG_FLAGS.HOME_V2_ENABLED]);
  }

  get isClaimCorrectFlagsEnabled(): boolean {
    return Boolean(this?.configFlags?.[APP_CONFIG_FLAGS.APP_CLAIM_TIMELINE_ACTIONS_ENABLED])
  }

  get headerConfigFlags(): Partial<IFCHeaderCapabilities> | undefined {
    return parseHeaderConfigFlags(
      this?.configFlags?.[APP_CONFIG_FLAGS.APP_TABS_VISIBILITY_CONTROLLER],
    );
  }

  // ZAMMAD_ENABLED is the ticketing master switch (Tickets tab).
  get isTicketingEnabled(): boolean {
    return (
      Boolean(this?.configFlags?.[APP_CONFIG_FLAGS.ZAMMAD_ENABLED])
    );
  }

  toggleSidebar = () => {
    this.isCollapsed = !this.isCollapsed;
    this.sidebarWidth = this.isCollapsed
      ? SIDEBAR_MIN_WIDTH
      : SIDEBAR_DEFAULT_WIDTH;
    this.saveState();
  };

  setContactSupportUploadFiles = (files: ContactSupportUploadFile[]) => {
    this.contactSupportUploadFiles = files;
  };

  setIsUploadSuccessful = (isSuccessful: boolean) => {
    this.isUploadSuccessful = isSuccessful;
  };

  setCollapsed = (collapsed: boolean) => {
    this.isCollapsed = collapsed;
    this.saveState();
  };

  setSidebarWidth = (width: number) => {
    // Clamp width between min and max
    const clampedWidth = Math.max(
      SIDEBAR_MIN_WIDTH,
      Math.min(SIDEBAR_MAX_WIDTH, width),
    );
    this.sidebarWidth = clampedWidth;
    this.saveState();
  };

  setIsResizing = (resizing: boolean) => {
    this.isResizing = resizing;
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  setSearchResults = (results: ChatSearchResult[]) => {
    this.searchResults = results;
  };

  setSearchLoading = (loading: boolean) => {
    this.searchLoading = loading;
  };

  setPolicyToPreview = (policy: IPolicyPreviewItem | null) => {
    this.policyToPreview = policy;
  };

  setContractToPreview = (contract: IContractPreviewItem | null) => {
    this.contractToPreview = contract;
  };

  setUserEmail = (email: string | null) => {
    this.userEmail = email;
  };

  get apiKeys(): ApiKeySettingItem[] {
    return this.apiKeySetting?.api_keys ?? [];
  }

  loadApiKeySetting = async () => {
    try {
      this.isApiKeySettingLoading = true;

      const response = await apiService.getApiKeySetting();

      if (response.success && response.data) {
        this.apiKeySetting = response.data;
        return;
      }

      throw new Error(response.error || "no data");
    } catch {
      // Leave apiKeySetting as-is; the dialog falls back to the empty state
    } finally {
      this.isApiKeySettingLoading = false;
    }
  };

  rotateApiKey = async (id: string) => {
    this.isRotatingApiKey = true;

    try {
      const response = await apiService.rotateApiKey(id);

      if (response.success && response.data) {
        const { secret, ...setting } = response.data;
        this.apiKeySetting = setting;
        this.rotatedApiKeySecret = secret;
        return;
      }

      throw new Error(response.error || "Failed to rotate key");
    } catch {
      // Nothing to recover from here; the dialog keeps showing the prior key state
    } finally {
      this.isRotatingApiKey = false;
    }
  };

  clearRotatedApiKeySecret = () => {
    this.rotatedApiKeySecret = null;
  };

  updateApiKeyAllowlist = async (id: string, ipAllowlist: string[]) => {
    this.isUpdatingApiKeyAllowlist = true;
    this.apiKeyAllowlistError = null;

    try {
      const response = await apiService.updateApiKeyAllowlist(id, ipAllowlist);

      if (response.success && response.data) {
        this.apiKeySetting = response.data;
        this.isUpdatingApiKeyAllowlist = false;
        return true;
      }

      throw new Error(response.error || "Failed to update allowlist");
    } catch {
      // Simulated fallback, used while the endpoint is unavailable
      if (this.apiKeySetting) {
        this.apiKeySetting = {
          ...this.apiKeySetting,
          api_keys: this.apiKeySetting.api_keys.map((key) =>
            key.id === id ? { ...key, ip_allowlist: ipAllowlist } : key,
          ),
        };
      }
      this.isUpdatingApiKeyAllowlist = false;
      return true;
    }
  };

  triggerUploadContactSupportFiles = async (
    formData: ContactSupportFormType & { purpose: string },
  ): Promise<{
    status: "success" | "error" | "warning";
    message?: string;
    ticket?: ITicket;
  }> => {
    // A retry that carries this upload's timestamp never files a ticket, so the
    // whole request has to be sent again; files not yet sent go back to attached.
    const filingFailed = (message?: string) => {
      this.setContactSupportUploadFiles(
        this.contactSupportUploadFiles.map((file) =>
          file.status === "uploading" ? { ...file, status: "attached" } : file,
        ),
      );
      const reason = message || "Support ticket failed!";
      return {
        status: "error" as const,
        message: `${/[.!?]$/.test(reason) ? reason : `${reason}.`} Please send your request again.`,
      };
    };
    let ticket: ITicket | undefined;
    try {
      if (this.contactSupportUploadFiles.length === 0) {
        const result = await apiService.uploadClaimAnalysisFiles(
          [],
          formData,
          null,
        );

        if (!result.success) {
          return filingFailed(result.message);
        }

        this.setIsUploadSuccessful(true);
        return {
          status: "success",
          message: "Support ticket created successfully",
          ticket: result.ticket,
        };
      }

      // Get only the files that are not uploaded/errored
      const files = this.contactSupportUploadFiles?.filter(
        (file) => file.status !== "uploaded",
      );

      // Update all files to uploading status for user feedback
      this.setContactSupportUploadFiles(
        this.contactSupportUploadFiles.map((file) =>
          files?.some((item) => item.id === file.id)
            ? ({
                ...file,
                status: "uploading",
              } as ContactSupportUploadFile)
            : file,
        ),
      );

      // Upload batches of files smaller than MAX_FILE_SIZE first
      const smallerFiles = files?.filter(
        (file) => file.file.size < API_CONFIG.UPLOAD_LIMITS.MAX_FILE_SIZE,
      );
      const largerFiles = files?.filter(
        (file) => file.file.size >= API_CONFIG.UPLOAD_LIMITS.MAX_FILE_SIZE,
      );

      let timestamp = null as string | null | undefined;

      // Upload smaller files in batches (each batch total size must not exceed MAX_FILE_SIZE)
      if (smallerFiles.length > 0) {
        const smallerFilesBatches: ContactSupportUploadFile[][] = [];
        let currentBatch: ContactSupportUploadFile[] = [];
        let currentBatchSize = 0;

        for (const file of smallerFiles) {
          const fileSize = file.file.size;
          if (
            currentBatchSize + fileSize >
              API_CONFIG.UPLOAD_LIMITS.MAX_FILE_SIZE &&
            currentBatch.length > 0
          ) {
            smallerFilesBatches.push(currentBatch);
            currentBatch = [];
            currentBatchSize = 0;
          }
          currentBatch.push(file);
          currentBatchSize += fileSize;
        }
        if (currentBatch.length > 0) {
          smallerFilesBatches.push(currentBatch);
        }

        for (const batch of smallerFilesBatches) {
          const filesTheTicket = !timestamp;
          const result = await apiService.uploadClaimAnalysisFiles(
            batch.map((file) => file.file),
            formData,
            timestamp,
          );

          if (result?.timestamp) {
            timestamp = result.timestamp;
          }
          ticket = result.ticket ?? ticket;

          // Update the status of the files in the batch
          const successFileIds = batch.map((file) => file.id);
          this.setContactSupportUploadFiles(
            this.contactSupportUploadFiles.map((file) =>
              successFileIds.includes(file.id)
                ? { ...file, status: result.success ? "uploaded" : "error" }
                : file,
            ),
          );

          if (filesTheTicket && !result.success) {
            return filingFailed(result.message);
          }
        }
      }

      // Upload larger files one by one
      if (largerFiles.length > 0) {
        for (const file of largerFiles) {
          const filesTheTicket = !timestamp;
          const result = await apiService.uploadClaimAnalysisFile(
            file.file,
            formData,
            timestamp,
          );

          if (result?.timestamp) {
            timestamp = result.timestamp;
          }
          ticket = result.ticket ?? ticket;

          this.setContactSupportUploadFiles(
            this.contactSupportUploadFiles.map((contractFile) =>
              contractFile.id === file.id
                ? {
                    ...contractFile,
                    status: result.success ? "uploaded" : "error",
                  }
                : contractFile,
            ),
          );

          if (filesTheTicket && !result.success) {
            return filingFailed(result.message);
          }
        }
      }

      if (
        this.contactSupportUploadFiles.every(
          (file) => file.status === "uploaded",
        )
      ) {
        this.setIsUploadSuccessful(true);
      }
    } catch (error) {
      return {
        status: "error",
        message:
          error instanceof Error ? error.message : "Failed to upload files",
      };
    }

    const erroredFilesCount = this.contactSupportUploadFiles?.filter(
      (file) => file.status === "error",
    );

    if (erroredFilesCount?.length === this.contactSupportUploadFiles?.length) {
      return {
        status: "error",
        message: "Fail to upload files",
      };
    }

    return {
      ticket,
      status: erroredFilesCount?.length > 0 ? "warning" : "success",
      message:
        erroredFilesCount?.length > 0
          ? "Some files failed to upload"
          : "Files uploaded successfully",
    };
  };

  private saveState = () => {
    localStorage.setItem(
      "appSidebarCollapsed",
      JSON.stringify(this.isCollapsed),
    );
    localStorage.setItem("appSidebarWidth", JSON.stringify(this.sidebarWidth));
  };

  private setLocalStorageData = () => {
    // Load initial state from localStorage if available
    const savedState = localStorage.getItem("appSidebarCollapsed");
    if (savedState !== null) {
      this.isCollapsed = JSON.parse(savedState);
    }
    // Load sidebar width from localStorage if available
    const savedWidth = localStorage.getItem("appSidebarWidth");

    if (savedWidth !== null) {
      const width = parseInt(savedWidth, 10);

      if (width >= SIDEBAR_MIN_WIDTH && width <= SIDEBAR_MAX_WIDTH) {
        this.sidebarWidth = width;
      }
    }
  };
}
